import { createReducer, on } from '@ngrx/store';
import { printersActions } from '../actions/printers.actions';
import { LabelDimensions, PrinterListItem, PrinterStatus } from '../models';

// Live status per printer id: 'loading' = query in flight, null = query failed, value = fetched.
export type PrinterRowStatus = PrinterStatus | 'loading' | null;

export interface PrintersState {
    printers: PrinterListItem[];
    statuses: Record<string, PrinterRowStatus>;
    labelDimensions: LabelDimensions[];
    loaded: boolean | null;
    loading: boolean | null;
}

export const initialState: PrintersState = {
    printers: [],
    statuses: {},
    labelDimensions: [],
    loaded: null,
    loading: null,
};

export const printersReducer = createReducer(
    initialState,
    on(printersActions.getPrinters, (state): PrintersState => ({
        ...state,
        loading: true,
        loaded: null,
    })),
    on(printersActions.printerStatusLoaded, (state, { id, status }): PrintersState => ({
        ...state,
        statuses: { ...state.statuses, [id]: status },
    })),
    on(printersActions.printerStatusError, (state, { id }): PrintersState => ({
        ...state,
        statuses: { ...state.statuses, [id]: null },
    })),
    on(printersActions.getPrintersSuccess, (state, { payload, withStatus }): PrintersState => ({
        ...state,
        loading: false,
        loaded: true,
        printers: payload,
        // A probed row keeps its last answer, so a re-open shows the known roll instead of a spinner.
        statuses: withStatus
            ? Object.fromEntries(payload.map((printer): [string, PrinterRowStatus] => [printer.id, state.statuses[printer.id] ?? 'loading']))
            : state.statuses,
    })),
    on(printersActions.getPrintersError, (state): PrintersState => ({
        ...state,
        loading: false,
        loaded: false,
    })),
    on(printersActions.getLabelDimensionsSuccess, (state, { payload }): PrintersState => ({
        ...state,
        labelDimensions: payload,
    })),
    // Sync from the single-printer store
    on(printersActions.printerRemoved, (state, { id }): PrintersState => ({
        ...state,
        printers: state.printers.filter((item) => item.id !== id),
    })),
    on(printersActions.clear, (): PrintersState => ({ ...initialState })),
);
